import React from 'react';
import { Slide } from '../components/deck/Slide';
import { Eyebrow, INK, Reveal, Title } from '../components/deck/Type';
const SERIF = {
  fontFamily: 'var(--font-serif)'
} as const;

/** Source team sheet (1920×1080). Headshots are cropped from it by circle centre. */
const TEAM_SHEET = 'https://cdn.magicpatterns.com/uploads/gFyoZYAn5mE1UD1RhmsjkJ/DiligenceGPT_Pitch_Deck_(2026).jpg';
const SHEET_W = 1920;
/** Source crop diameter in px — kept inside the original circles. */
const CROP = 104;
/** Rendered avatar diameter, in stage cqw. */
const AVATAR = 5.2;
type Member = {
  name: string;
  credential?: string;
  role: string;
  logos: string[];
  fullTime: boolean;
  /** Headshot centre in the source sheet, px. Omitted → monogram. */
  face?: {
    x: number;
    y: number;
  };
};
const team: Member[] = [{
  name: 'Ashley Martis',
  credential: 'EMBA',
  role: 'CEO & Founder',
  logos: ['TD', 'IBM', 'Schulich School of Business'],
  fullTime: true,
  face: {
    x: 985,
    y: 598
  }
}, {
  name: 'Rishi Sareen',
  credential: 'EMBA',
  role: 'COO',
  logos: ['Schulich School of Business', 'Ivey Business School'],
  fullTime: true,
  face: {
    x: 292,
    y: 600
  }
}, {
  name: 'Maaz Rehman',
  role: 'CTO',
  logos: ['Microsoft'],
  fullTime: true,
  face: {
    x: 1363,
    y: 604
  }
}, {
  name: 'Ian Dela Cruz',
  role: 'Director of Sales',
  logos: ['Morningstar', 'PitchBook'],
  fullTime: false
}, {
  name: 'Sumit Madan',
  role: 'Director of Enterprise Sales',
  logos: ['Shopify'],
  fullTime: false
}, {
  name: 'Wendy Huang',
  role: 'Venture Associate',
  logos: ['Sony', 'Rotman School of Management'],
  fullTime: true
}, {
  name: 'AJ Noronha',
  role: 'US Venture Partner',
  logos: ['University of Notre Dame'],
  fullTime: false
}];
function Headshot({
  member
}: {
  member: Member;
}) {
  const size = {
    width: `${AVATAR}cqw`,
    height: `${AVATAR}cqw`
  };
  if (!member.face) {
    const initials = member.name.split(' ').filter(part => /^[A-Z]/.test(part)).map(part => part[0]).slice(0, 2).join('');
    return <div role="img" aria-label={`${member.name} monogram`} className="flex shrink-0 items-center justify-center rounded-full border border-white/15 bg-[#0E1526]" style={size} data-id="element-525" data-mp="2bgsaytd7qs69">
        <span className="text-[1.5cqw] leading-none text-[#AEB7C7]" style={SERIF} data-id="element-526" data-mp="22jrois9pf45g">
          {initials}
        </span>
      </div>;
  }
  const unit = AVATAR / CROP;
  const left = member.face.x - CROP / 2;
  const top = member.face.y - CROP / 2;
  return <div role="img" aria-label={`Portrait of ${member.name}`} className="shrink-0 rounded-full border border-white/15 bg-[#0B1120]" style={{
    ...size,
    backgroundImage: `url("${TEAM_SHEET}")`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: `${SHEET_W * unit}cqw auto`,
    backgroundPosition: `${-left * unit}cqw ${-top * unit}cqw`
  }} data-id="element-527" data-mp="2pgjrb0r7cknf" />;
}
function TeamCard({
  member,
  i
}: {
  member: Member;
  i: number;
}) {
  return <Reveal delay={0.04 * i} className="flex h-full flex-col border border-white/10 bg-white/[0.025] px-[1.4cqw] pb-[2.2cqh] pt-[2.4cqh]" data-id="element-528" data-mp="14oxhsbpdlu8n">
      <div className="flex items-start justify-between" data-id="element-529" data-mp="1dokievaa6w0n">
        <Headshot member={member} data-id="element-530" data-mp="1dq5ame94i2vc" />
        <span className="text-[0.72cqw] uppercase tracking-[0.2em]" style={{
        color: member.fullTime ? INK.blue : '#7C8699'
      }} data-id="element-531" data-mp="1inoqfilgynpe">
          {member.fullTime ? '* Full-Time' : 'Part-Time'}
        </span>
      </div>

      <h3 className="mt-[2.2cqh] text-[1.6cqw] leading-tight text-[#F4F7FC]" style={SERIF} data-id="element-532" data-mp="1seje8red5vcy">
        {member.name}
      </h3>
      <p className="mt-[1cqh] text-[0.86cqw] uppercase tracking-[0.16em] text-[#C3CBD9]" data-id="element-533" data-mp="1weaavbqkn5pe">
        {member.credential && <span className="mr-[0.5cqw] text-[#7C8699]" data-id="element-534" data-mp="21cbtxwnzz50v">{member.credential} ·</span>}
        {member.role}
      </p>

      <div className="mt-auto border-t border-white/10 pt-[1.6cqh]" data-id="element-535" data-mp="1sq0spuejab9e">
        <ul className="flex flex-wrap items-center gap-x-[0.9cqw] gap-y-[0.6cqh]" data-id="element-536" data-mp="1so65uajpc92o">
          {member.logos.map((logo, li) => <li key={logo} className="flex items-center gap-[0.9cqw]" data-id="element-537" data-mp="22gvgj37fhiwy" data-mpi={li}>
              {li > 0 && <span className="h-[1.6cqh] w-px bg-white/15" aria-hidden data-id="element-538" data-mp="1xh90yn44nqkz" />}
              <span className="text-[0.86cqw] font-medium tracking-[0.02em] text-[#8B95A8]" data-id="element-539" data-mp="1xjc2o4en785j">
                {logo}
              </span>
            </li>)}
        </ul>
      </div>
    </Reveal>;
}
export function TeamSlide() {
  const firstRow = team.slice(0, 4);
  const secondRow = team.slice(4);
  return <Slide data-id="element-540" data-mp="1eqrciuqbvh5f">
      <Eyebrow data-id="element-541" data-mp="1er13akj75sc6">Team</Eyebrow>
      <Title className="mt-[2.2cqh]" data-id="element-542" data-mp="1eppz4jluaikj">
        Built by operators across venture, enterprise &amp; technology.
      </Title>

      <div className="mt-auto flex flex-col gap-[1.6cqh]" data-id="element-543" data-mp="2v36gcbn7nfsp">
        <div className="grid h-[31cqh] grid-cols-4 gap-[1.2cqw]" data-id="element-544" data-mp="2q6pq9bwgarcz">
          {firstRow.map((m, i) => <TeamCard key={m.name} member={m} i={i} data-id="element-545" data-mp="2xoe6njyajf6a" data-mpi={i} />)}
        </div>
        <div className="mx-auto grid h-[31cqh] w-[calc(75%-0.3cqw)] grid-cols-3 gap-[1.2cqw]" data-id="element-546" data-mp="32p1vq02yllxv">
          {secondRow.map((m, i) => <TeamCard key={m.name} member={m} i={i + 4} data-id="element-547" data-mp="2oq2h7l95unff" data-mpi={i} />)}
        </div>
      </div>

      <p className="mt-[2.2cqh] text-[0.82cqw] text-[#7C8699]" data-id="element-548" data-mp="2sr47vdp0hr5s">
        <span className="text-[#3B8CFF]" data-id="element-549" data-mp="3gg6wx6lz6mab">*</span> Full-Time Team Member · All
        others are Part-Time / Venture Partners
      </p>
    </Slide>;
}